import classnames from 'classnames/bind'
import { useTranslate } from 'react-polyglot'
import { GlobalThemeColors } from '~/@types/colors'
import { GlobalTextPreset } from '~/@types/text-preset'
import { GlobalTextStyling } from '~/@types/text-styling'

import { CloseIcon, TextEditIcon } from '~/components/UI/Icons'
import Image from '~/components/UI/Image'

import { usePackProductsProvider } from '~/providers/PackProductsProvider'
import { useStyle } from '~/providers/StyleProvider'
import { useUserSelectionProvider } from '~/providers/UserSelectionProvider'

import { useGetStateInformationsFromUserSelection } from '~/hooks/useGetStateInformationsFromUserSelection'

import { CUSTOMIZER } from '~/data/dictionary'

import css from './styles.module.scss'

const cx = classnames.bind(css)

export interface MultiBundleSelectorProps {
  className?: string
  index?: number
  clickFunction?(): void
}

export function MultiBundleSelector({
  className,
  index,
  clickFunction,
}: MultiBundleSelectorProps) {
  const t = useTranslate()
  const { userSelection, setUserSelection } = useUserSelectionProvider()
  const { data } = usePackProductsProvider()
  const { activeTabIndex } = useGetStateInformationsFromUserSelection()
  const tab = userSelection[index]
  const activeProduct = data[index]?.find(
    (product) => product.shopifyId === tab.productId,
  )

  const isActive = activeTabIndex === index
  const activeVariant =
    activeProduct?.variants?.find((variant) => variant.id === tab.variantId) ??
    null
  const hasMultipleVariants = activeProduct?.variants?.length > 1
  const isCustomizable = activeProduct?.byCharlotCustomizable
  const customizerVariants = isCustomizable
    ? activeProduct.customizationProducts
        ?.map((product) => ({
          type: product?.productType,
          variant: product?.variants?.find(
            (variant) => variant.id === tab[product.productType],
          ),
        }))
        .filter((entry) => entry.variant)
    : []

  const titleStyle = useStyle({
    textPreset: GlobalTextPreset.Title10Primary,
    textStyling: GlobalTextStyling.UpperCase,
  })
  const variantsStyle = useStyle({
    textPreset: GlobalTextPreset.Text12Secondary,
    color: GlobalThemeColors.Gray1ToBlack,
  })

  return (
    <button
      className={cx(css.MultiBundleSelector, className)}
      aria-label={t(
        isActive
          ? CUSTOMIZER.CUSTOMIZE_MULTI_BUNDLE_ITEM
          : CUSTOMIZER.CLOSE_MULTI_BUNDLE_ITEM,
        { index: index + 1 },
      )}
      onClick={() => {
        setUserSelection((prev) => {
          return prev.map((tab, i) => {
            return {
              ...tab,
              isActive: i === index && !prev[i].isActive,
            }
          })
        })
        clickFunction?.()
      }}>
      <Image
        className={cx(css.image)}
        {...(isCustomizable && customizerVariants.length > 0
          ? customizerVariants?.[0]?.variant?.variantImage
          : activeVariant?.image)}
        ratio={1 / 1}
        priority={index === 0}
        layout="fill"
        sizes="80px"
        objectFit="cover"
      />
      <div className={cx(css.content)}>
        <span className={cx(css.title, titleStyle)}>
          {activeProduct?.title}
        </span>

        {customizerVariants?.length > 0 && (
          <div className={cx(css.variants, variantsStyle)}>
            {customizerVariants?.map(({ type, variant }, i) => {
              return variant ? (
                <span key={`variant_${i}`}>
                  <span className={cx(css.type)}>{`${type} : `}</span>
                  {`${variant?.selectedOptions?.[0]?.value}`}
                </span>
              ) : null
            })}
          </div>
        )}
        {customizerVariants?.length === 0 &&
          activeVariant &&
          hasMultipleVariants && (
            <div className={cx(css.variants, variantsStyle)}>
              {`
          ${activeVariant?.selectedOptions?.[0].name} :
          ${activeVariant?.selectedOptions?.[0].value}
        `}
            </div>
          )}
      </div>

      <div className={cx(css.iconContainer)}>
        {isActive ? (
          <CloseIcon className={cx(css.closeIcon)} />
        ) : (
          <TextEditIcon className={cx(css.editIcon)} />
        )}
      </div>
    </button>
  )
}
